// src/components/forms/mapping-editor/TextFieldMappingsEditor.tsx (Create this file)
import React, { useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrashAlt, faList, faICursor } from '@fortawesome/free-solid-svg-icons';
import FormInput from '../formBuilder/FormInput';
import { TextFieldMappingValue } from '../../../../interfaces/dynamicForms/PdfMapping';
// Assuming TextFieldMappingValue = string | string[] | any (defined in PdfMapping.ts)
// We'll treat 'any' as string | string[] for implementation

interface TextFieldMappingsEditorProps {
    mappings: Record<string, TextFieldMappingValue> | undefined;
    updateMappings: (newMappings: Record<string, TextFieldMappingValue>) => void;
}

// Helper to manage the value input state for the "Add New" form
type NewValueType = 'single' | 'multiple';

const TextFieldMappingsEditor: React.FC<TextFieldMappingsEditorProps> = ({ mappings = {}, updateMappings }) => {
    // State for the "Add New" form inputs
    const [newFormFieldId, setNewFormFieldId] = useState('');
    const [newValueType, setNewValueType] = useState<NewValueType>('single');
    const [newSingleValue, setNewSingleValue] = useState('');
    const [newMultiValues, setNewMultiValues] = useState<string[]>(['']); // Start with one empty input for multi
    const [addError, setAddError] = useState<string | null>(null);

    // Handler for changes within an existing mapping's row
    const handleMappingChange = useCallback((
        formFieldId: string, // The key
        newValue: string | string[] // The new value (string or array)
    ) => {
        // Create a new top-level mappings object for immutability
        const newMappings = {
            ...mappings,
            [formFieldId]: newValue,
        };
        updateMappings(newMappings);
    }, [mappings, updateMappings]);

    // Handler to remove an existing mapping
    const handleRemoveMapping = useCallback((formFieldIdToRemove: string) => {
        const newMappings = { ...mappings };
        delete newMappings[formFieldIdToRemove];
        updateMappings(newMappings);
    }, [mappings, updateMappings]);

    // Handler to add a new mapping from the "Add New" inputs
    const handleAddMapping = useCallback(() => {
        setAddError(null);
        const key = newFormFieldId.trim();
        if (!key) {
            setAddError('Form Field ID cannot be empty.');
            return;
        }
        if (mappings[key] !== undefined) {
            setAddError(`Form Field ID "${key}" already exists.`);
            return;
        }

        let valueToAdd: string | string[];
        if (newValueType === 'single') {
            if (!newSingleValue.trim()) {
                setAddError('PDF Field Name cannot be empty for single value.');
                return;
            }
            valueToAdd = newSingleValue.trim();
        } else { // multiple
            const validMultiValues = newMultiValues.map(v => v.trim()).filter(v => v); // Trim and filter empty
            if (validMultiValues.length === 0) {
                 setAddError('At least one PDF Field Name must be provided for multiple values.');
                 return;
            }
            valueToAdd = validMultiValues;
        }

        const newMappings = { ...mappings, [key]: valueToAdd };
        updateMappings(newMappings);

        // Reset add form
        setNewFormFieldId('');
        setNewValueType('single');
        setNewSingleValue('');
        setNewMultiValues(['']);
    }, [mappings, updateMappings, newFormFieldId, newValueType, newSingleValue, newMultiValues]);

    // Helper to render the value input(s) for an existing mapping row
    const renderValueInputs = (key: string, value: TextFieldMappingValue) => {
        const isArray = Array.isArray(value);

        const handleSingleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            handleMappingChange(key, e.target.value);
        };

        const handleMultiChange = (index: number, itemValue: string) => {
             const currentArray = isArray ? [...value] : [value || '']; // Ensure it's an array
             currentArray[index] = itemValue;
             // Filter out empty strings before saving? Or allow them? Let's allow temporarily.
             handleMappingChange(key, currentArray); //.filter(v => v));
        };

         const handleAddMultiItem = () => {
             const currentArray = isArray ? [...value, ''] : [(value || ''), ''];
             handleMappingChange(key, currentArray);
         };

          const handleRemoveMultiItem = (index: number) => {
             if (!isArray) return; // Should not happen if called correctly
             const currentArray = value.filter((_, i) => i !== index);
             handleMappingChange(key, currentArray);
         };

         const toggleType = () => {
             if (isArray) {
                 // Convert array (even empty) to single empty string
                 handleMappingChange(key, value[0] || '');
             } else {
                 // Convert single string to array containing that string (or empty array if string was empty)
                 handleMappingChange(key, value ? [value] : ['']);
             }
         }

        return (
            <div className="md:col-span-4"> {/* Span across multiple columns */}
                 <div className="flex items-center mb-1">
                    <label className="block text-xs font-medium text-gray-600 mr-4">PDF Field(s)</label>
                    <button
                        onClick={toggleType}
                        className={`px-2 py-0.5 text-xs rounded ${isArray ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'} hover:bg-gray-200`}
                        title={isArray ? "Switch to Single Field" : "Switch to Multiple Fields"}
                    >
                       <FontAwesomeIcon icon={isArray ? faICursor : faList} className="mr-1" />
                       {isArray ? 'Multiple' : 'Single'}
                    </button>
                 </div>
                {isArray ? (
                    <div className="space-y-1">
                        {(value as string[]).map((itemValue, index) => (
                            <div key={index} className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={itemValue}
                                    onChange={(e) => handleMultiChange(index, e.target.value)}
                                    placeholder={`PDF Field ${index + 1}`}
                                    className="flex-grow block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <button
                                    type="button"
                                    onClick={() => handleRemoveMultiItem(index)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400"
                                    title="Remove Field"
                                    disabled={(value as string[]).length <= 1} // Don't allow removing the last one easily, add another first
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </div>
                        ))}
                        <button
                            type="button"
                            onClick={handleAddMultiItem}
                            className="text-blue-600 hover:text-blue-800 text-xs px-1 py-0.5"
                        >
                           + Add PDF Field
                        </button>
                    </div>
                ) : (
                    <input
                        type="text"
                        value={(value as string) || ''}
                        onChange={handleSingleChange}
                        placeholder="PDF Field Name"
                         className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500"
                    />
                )}
            </div>
        );
    };

    // --- Add New Mapping Inputs ---
    const renderNewValueInputs = () => {
        const handleMultiChange = (index: number, itemValue: string) => {
             const newArray = [...newMultiValues];
             newArray[index] = itemValue;
             setNewMultiValues(newArray);
         };

         const handleAddMultiItem = () => setNewMultiValues([...newMultiValues, '']);
         const handleRemoveMultiItem = (index: number) => setNewMultiValues(newMultiValues.filter((_, i) => i !== index));

         return (
             <div className="md:col-span-4">
                 <div className="flex items-center mb-1">
                     <label className="block text-xs font-medium text-gray-600 mr-4">PDF Field(s)</label>
                     <button
                         onClick={() => setNewValueType(newValueType === 'single' ? 'multiple' : 'single')}
                         className={`px-2 py-0.5 text-xs rounded ${newValueType === 'multiple' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'} hover:bg-gray-200`}
                         title={newValueType === 'multiple' ? "Switch to Single Field" : "Switch to Multiple Fields"}
                     >
                        <FontAwesomeIcon icon={newValueType === 'multiple' ? faICursor : faList} className="mr-1" />
                        {newValueType === 'multiple' ? 'Multiple' : 'Single'}
                     </button>
                 </div>
                 {newValueType === 'multiple' ? (
                     <div className="space-y-1">
                        {newMultiValues.map((itemValue, index) => (
                            <div key={index} className="flex items-center space-x-2">
                                <input
                                    type="text"
                                    value={itemValue}
                                    onChange={(e) => handleMultiChange(index, e.target.value)}
                                    placeholder={`PDF Field ${index + 1}`}
                                    className="flex-grow block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500"
                                />
                                <button
                                     type="button"
                                     onClick={() => handleRemoveMultiItem(index)}
                                     className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400"
                                     title="Remove Field"
                                     disabled={newMultiValues.length <= 1}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} />
                                </button>
                            </div>
                         ))}
                         <button type="button" onClick={handleAddMultiItem} className="text-blue-600 hover:text-blue-800 text-xs px-1 py-0.5">+ Add PDF Field</button>
                     </div>
                 ) : (
                     <input
                         type="text"
                         value={newSingleValue}
                         onChange={(e) => setNewSingleValue(e.target.value)}
                         placeholder="PDF Field Name"
                         className="block w-full px-2 py-1 border border-gray-300 rounded-md shadow-sm sm:text-sm focus:ring-indigo-500 focus:border-indigo-500"
                     />
                 )}
             </div>
         );
    };


    const mappingEntries = Object.entries(mappings);

    return (
        <div className="p-4 border rounded bg-white shadow-sm mb-6">
            <h3 className="text-lg font-semibold mb-1 text-gray-700">Text Field Mappings</h3>
            <p className="text-xs text-gray-500 mb-4">
                Map form field IDs directly to one or more PDF text field names.
            </p>

            {/* List of existing mappings */}
            <div className="space-y-3 mb-4">
                {mappingEntries.length > 0 ? (
                    mappingEntries.map(([formFieldId, value]) => (
                        <div key={formFieldId} className="border rounded p-3 bg-gray-50 relative grid grid-cols-1 md:grid-cols-7 gap-3 items-start">
                           {/* Form Field ID */}
                           <div className="md:col-span-2">
                               <FormInput
                                   label="Form Field ID"
                                   id={`tfmap-form-${formFieldId}`}
                                   value={formFieldId}
                                   readOnly
                                   className="bg-gray-100 text-gray-600"
                                   title="To change Form Field ID, remove and re-add the mapping."
                               />
                           </div>
                           {/* PDF Field(s) Input(s) */}
                           {renderValueInputs(formFieldId, value)}
                            {/* Remove Button */}
                            <div className="md:col-span-1 flex items-center justify-end pt-6"> {/* Align with inputs */}
                                <button
                                    type="button"
                                    onClick={() => handleRemoveMapping(formFieldId)}
                                    className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-400"
                                    title={`Remove mapping for ${formFieldId}`}
                                >
                                    <FontAwesomeIcon icon={faTrashAlt} /> Remove
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <p className="text-sm text-gray-500 italic">No text field mappings added yet.</p>
                )}
            </div>

            {/* Form to Add New Mapping */}
            <div className="border-t pt-4">
                <h4 className="text-md font-medium mb-2 text-gray-600">Add New Text Field Mapping</h4>
                <div className="grid grid-cols-1 md:grid-cols-7 gap-3 items-end">
                    <div className="md:col-span-2">
                        <FormInput
                            label="Form Field ID"
                            id="tfmap-new-form"
                            value={newFormFieldId}
                            onChange={(e) => setNewFormFieldId(e.target.value)}
                            placeholder="e.g., dateOfContact"
                            required
                        />
                    </div>
                    {/* PDF Field(s) Input(s) for New Item */}
                    {renderNewValueInputs()}
                    {/* Add Button */}
                    <div className="md:col-span-1 pb-1">
                        <button
                            type="button"
                            onClick={handleAddMapping}
                            className="w-full inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                            <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                            <span className="ml-2">Add</span>
                        </button>
                    </div>
                </div>
                {addError && <p className="text-xs text-red-600 mt-1">{addError}</p>}
            </div>
        </div>
    );
};

export default TextFieldMappingsEditor;